import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import { CircularProgress, Box } from '@mui/material';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import ThemeProvider from './contexts/ThemeContext';
import { initializeRequiredCollections } from './firebase/initCollections';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ServiceManagement from './pages/ServiceManagement';
import WorkRequestList from './pages/WorkRequestList';
import WorkRequestFormPage from './pages/WorkRequestFormPage';
import PlanificationPage from './pages/PlanificationPage';
import AdminDashboard from './pages/AdminDashboard';
import SchoolManagement from './pages/SchoolManagement';
import TechnicalServiceDashboardNew from './pages/TechnicalServiceDashboardNew';
import MobileServiceApp from './pages/MobileServiceApp';
import TechnicalServiceAnalytics from './pages/TechnicalServiceAnalytics';
import SectorManagement from './pages/SectorManagement';
import PublicScanForm from './pages/PublicScanForm';
import NotFound from './pages/NotFound';

// Components
import Navbar from './components/layout/Navbar';
import { USER_ROLES } from './models/School';

const LoadingScreen = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
    <CircularProgress />
  </Box>
);

function ProtectedRoute({ children, allowedRoles = [] }) {
  const { currentUser, userRole, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (currentUser) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function MainLayout({ children }) {
  const { currentUser } = useAuth();

  return (
    <>
      {currentUser && <Navbar />}
      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: currentUser ? 8 : 0 }}>
        {children}
      </Box>
    </>
  );
}

function App() {
  // Initialiser les collections Firestore requises au démarrage de l'application
  useEffect(() => {
    const initCollections = async () => {
      try {
        await initializeRequiredCollections();
        console.log('Collections Firestore initialisées avec succès');
      } catch (error) {
        console.error('Erreur lors de l\'initialisation des collections Firestore:', error);
      }
    };
    
    initCollections();
  }, []);
  
  return (
    <ThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
        <AuthProvider>
          <Router>
            <MainLayout>
              <Routes>
                <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/services" element={<ProtectedRoute allowedRoles={[USER_ROLES.ADMIN]}><ServiceManagement /></ProtectedRoute>} />
                <Route path="/demandes" element={<ProtectedRoute><WorkRequestList /></ProtectedRoute>} />
                <Route path="/nouvelle-demande" element={<ProtectedRoute><WorkRequestFormPage /></ProtectedRoute>} />
                
                {/* Route unifiée pour la planification (remplace /calendrier et /planning) */}
                <Route path="/planification" element={<ProtectedRoute><PlanificationPage /></ProtectedRoute>} />
                
                {/* Redirections pour compatibilité avec les anciennes URLs */}
                <Route path="/calendrier" element={<Navigate to="/planification" replace />} />
                <Route path="/planning" element={<Navigate to="/planification" replace />} />
                
                <Route path="/admin" element={<ProtectedRoute allowedRoles={[USER_ROLES.ADMIN]}><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin-dashboard" element={<ProtectedRoute allowedRoles={[USER_ROLES.SUPER_ADMIN]}><AdminDashboard /></ProtectedRoute>} />
                <Route path="/schools" element={<ProtectedRoute allowedRoles={[USER_ROLES.SUPER_ADMIN]}><SchoolManagement /></ProtectedRoute>} />
                <Route path="/ecoles" element={<ProtectedRoute allowedRoles={[USER_ROLES.ADMIN]}><SchoolManagement /></ProtectedRoute>} />
                <Route path="/service-technique" element={<ProtectedRoute allowedRoles={[USER_ROLES.TECHNICAL_SERVICE]}><TechnicalServiceDashboardNew /></ProtectedRoute>} />
                <Route path="/service-mobile" element={<ProtectedRoute allowedRoles={[USER_ROLES.TECHNICAL_SERVICE]}><MobileServiceApp /></ProtectedRoute>} />
                <Route path="/service-analytics" element={<ProtectedRoute allowedRoles={[USER_ROLES.TECHNICAL_SERVICE]}><TechnicalServiceAnalytics /></ProtectedRoute>} />
                <Route path="/secteurs" element={<ProtectedRoute allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}><SectorManagement /></ProtectedRoute>} />

                {/* Route publique QR code — pas de login requis */}
                <Route path="/scan/:token" element={<PublicScanForm />} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </MainLayout>
          </Router>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
